import { TelematicsInstructions, SampleBadge } from "../shared/SampleDataNotice";
import React from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, AreaChart, Area, PieChart as RPie, Pie, Cell } from "recharts";
import { useQuery } from "@tanstack/react-query";
import { MapPin, CalendarClock, BellRing, PieChart, TrendingUp } from "lucide-react";
import "leaflet/dist/leaflet.css";
import { api } from "../../lib/api";
import type { Dashboard } from "../../lib/types";
import { CATEGORY_LABEL } from "../../lib/utils";
import { Card } from "../core/Layout";

const PALETTE = ["#10046E", "#EEBD0A", "#0E7490", "#DC2626", "#059669", "#7C3AED", "#F97316", "#64748B"];
const tip = { contentStyle: { fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" } };

export function ExpiryTimelineChart({ data }: { data: Dashboard["expiry_timeline"] }) {
  const rows = data || [];
  const series = Array.from(new Set(rows.flatMap((r) => Object.keys(r).filter((k) => k !== "month" && k !== "label"))));
  const empty = rows.every((r) => series.every((s) => !r[s]));
  return (
    <Card icon={CalendarClock} title="Renewals due · next 6 months" testId="expiry-timeline-card">
      {empty ? <p className="text-sm text-slate-500">No renewals fall due in the next 6 months.</p> : (
        <div className="h-56" data-testid="expiry-timeline-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} /><YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <RTooltip {...tip} /><Legend wrapperStyle={{ fontSize: 11 }} />
              {series.map((s, i) => <Bar key={s} dataKey={s} stackId="a" fill={PALETTE[i % PALETTE.length]} radius={i === series.length - 1 ? [4, 4, 0, 0] : 0} />)}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

export function AlertsTrendChart({ data }: { data: Dashboard["alerts_trend"] }) {
  return (
    <Card icon={BellRing} title="Alerts raised · last 14 days" testId="alerts-trend-card">
      <div className="h-44" data-testid="alerts-trend-chart">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data || []} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
            <defs><linearGradient id="alertsFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#DC2626" stopOpacity={0.35} /><stop offset="100%" stopColor="#DC2626" stopOpacity={0} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis dataKey="day" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} interval={2} /><YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
            <RTooltip {...tip} />
            <Area type="monotone" dataKey="alerts" stroke="#DC2626" strokeWidth={2} fill="url(#alertsFill)" name="Raised" />
            <Area type="monotone" dataKey="resolved" stroke="#059669" strokeWidth={2} fill="transparent" name="Resolved" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

export function CompositionDonut({ byCategory, byStatus }: { byCategory: Record<string, number>; byStatus: Record<string, number> }) {
  const cat = Object.entries(byCategory).filter(([, n]) => n > 0).map(([k, v]) => ({ name: CATEGORY_LABEL[k] || k, value: v }));
  const st = Object.entries(byStatus).map(([k, v]) => ({ name: k, value: v }));
  const total = cat.reduce((a, b) => a + b.value, 0);
  const STATUS_COLOR: Record<string, string> = { active: "#059669", maintenance: "#F59E0B", grounded: "#DC2626" };
  return (
    <Card icon={PieChart} title="Fleet composition" testId="composition-card">
      <div className="grid grid-cols-2 gap-2">
        <div className="relative h-40" data-testid="composition-chart">
          <ResponsiveContainer width="100%" height="100%"><RPie><Pie data={cat} dataKey="value" innerRadius={42} outerRadius={64} paddingAngle={2} stroke="none">{cat.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}</Pie><RTooltip {...tip} /></RPie></ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"><span className="text-xl font-bold text-slate-900">{total}</span><span className="text-[10px] uppercase tracking-wider text-slate-500">vehicles</span></div>
        </div>
        <div className="relative h-40" data-testid="status-chart">
          <ResponsiveContainer width="100%" height="100%"><RPie><Pie data={st} dataKey="value" innerRadius={42} outerRadius={64} paddingAngle={2} stroke="none">{st.map((s, i) => <Cell key={i} fill={STATUS_COLOR[s.name] || PALETTE[i]} />)}</Pie><RTooltip {...tip} /></RPie></ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"><span className="text-xl font-bold text-emerald-700">{byStatus.active || 0}</span><span className="text-[10px] uppercase tracking-wider text-slate-500">active</span></div>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
        {cat.map((c, i) => <span key={c.name} className="inline-flex items-center gap-1 text-slate-600"><span className="h-2 w-2 rounded-sm" style={{ background: PALETTE[i % PALETTE.length] }} />{c.name} <b>{c.value}</b></span>)}
        {st.map((s) => <span key={s.name} className="inline-flex items-center gap-1 capitalize text-slate-600"><span className="h-2 w-2 rounded-full" style={{ background: STATUS_COLOR[s.name] }} />{s.name} <b>{s.value}</b></span>)}
      </div>
    </Card>
  );
}

export function GrowthChart({ data, title = "Vehicles added · last 6 months" }: { data: Dashboard["vehicle_growth"]; title?: string }) {
  return (
    <Card icon={TrendingUp} title={title} testId="growth-card">
      <div className="h-40" data-testid="growth-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data || []} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} /><YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
            <RTooltip {...tip} /><Bar dataKey="added" name="Added" fill="var(--brand)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

interface Pos { vehicle_id: string; plate_number: string; status: string; lat: number; lng: number; speed_kmh?: number | null; ignition?: boolean | null; simulated: boolean }

export function MiniLiveMap({ tenantId, base, enabled = true }: { tenantId?: string; base: string; enabled?: boolean }) {
  const { data } = useQuery<Pos[]>({ queryKey: ["telematics-positions", tenantId || ""], queryFn: async () => (await api.get("/telematics/vehicles", { params: { tenant_id: tenantId || undefined } })).data, refetchInterval: 60000, retry: false, enabled });
  const live = (data || []).filter((p) => !p.simulated);
  const moving = live.filter((p) => p.ignition).length;
  const color = (p: Pos) => (p.simulated ? "#94a3b8" : p.ignition ? "#059669" : "#0284c7");
  return (
    <Card icon={MapPin} title="Live fleet map" testId="dashboard-map-card" actions={<Link to={`${base}/telematics`} className="text-xs font-semibold text-[color:var(--brand)] hover:underline" data-testid="dashboard-map-link">Open Live Map →</Link>}>
      {data && live.length === 0 && <div className="mb-3"><TelematicsInstructions connected={false} compact testId="dashboard-map-sample-notice" /></div>}
      <div className={`relative h-72 overflow-hidden rounded-md border border-slate-200 ${data && live.length === 0 ? "grayscale-[0.6] opacity-80" : ""}`} data-testid="dashboard-map">
        {data && live.length === 0 && <div className="pointer-events-none absolute right-3 top-3 z-[400]"><SampleBadge testId="dashboard-map-sample-badge" /></div>}
        <MapContainer center={[3.12, 101.65]} zoom={10} className="h-full w-full" scrollWheelZoom={false} zoomControl={false} attributionControl={false}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {(data || []).map((p) => <CircleMarker key={p.vehicle_id} center={[p.lat, p.lng]} radius={p.simulated ? 5 : 8} pathOptions={{ color: "#fff", weight: 2, fillColor: color(p), fillOpacity: p.simulated ? 0.55 : 0.95 }}><Tooltip direction="top"><span className="font-mono text-xs font-bold">{p.plate_number}</span>{!p.simulated && ` · ${p.ignition ? `${p.speed_kmh} km/h` : "parked"}`}</Tooltip></CircleMarker>)}
        </MapContainer>
        <div className="pointer-events-none absolute left-3 top-3 z-[400] flex gap-2 rounded bg-white/90 px-2.5 py-1.5 text-[11px] font-semibold shadow" data-testid="dashboard-map-stats"><span className="text-emerald-700">{moving} moving</span><span className="text-sky-700">{live.length - moving} parked</span><span className="text-slate-500">{(data?.length || 0) - live.length} simulated</span></div>
      </div>
    </Card>
  );
}
